import React, { useMemo } from 'react'
import { Col, Form, Row } from 'antd'
import SearchField from '../../../common/searchFields/SearchField'
import { useTranslation } from 'react-i18next'
import SEARCH_REFERRAL_SOURCES from '@dentolize/clinic-mobile/src/shared/store/queries/actions/referralSources/searchReferralSources'
import SEARCH_REFERRAL_SUB_SOURCES from '@dentolize/clinic-mobile/src/shared/store/queries/actions/referralSubSources/searchReferralSubSources'
import * as PropTypes from 'prop-types'
import { gql, useApolloClient } from '@apollo/client'
import { SEARCH_PATIENT_QUERY, SEARCH_USERS_QUERY } from '@dentolize/clinic-mobile/src/shared/store/queries/appointmentQueries'
import { useUser } from '../../../../context/userContext'
import NameField from '../../../common/formFields/NameField'

const ReferralSourceField = ({
  required,
  visible,
  form,
  hidePatient,
  hideUser,
  companyId,
  leadLink,
  referralDetailsRequired,
  referralDetailsVisible
}) => {
  const { user } = useUser()
  const client = useApolloClient()
  const { t } = useTranslation()

  const referralSource = Form.useWatch('referralSource', form)

  const { setFieldsValue, setFieldValue } = form

  const resetSubReferral = () => {
    setFieldValue('referralSubSource', null)
  }

  const referralSourceData = useMemo(
    () =>
      referralSource
        ? client.readFragment(
            {
              id: `ReferralSource:${referralSource?.key}`,
              fragment: gql`
                fragment ReferralSourceData on ReferralSource {
                  id
                  name
                  forPatients
                  forceSubtype
                  forUsers
                }
              `
            },
            true
          )
        : null,
    [referralSource]
  )

  return visible === false ? null : (
    <Row type="flex" gutter={16}>
      <Col md={8} xs={24}>
        <SearchField
          GET_QUERY={SEARCH_REFERRAL_SOURCES}
          label={t('patients.referral')}
          fieldName="referralSource"
          dataName="searchReferralSources"
          rules={[{ required, message: t('app.fieldRequired') }]}
          allowClear
          onSelect={resetSubReferral}
          setFieldsValue={setFieldsValue}
          passToQuery={{ companyId, leadLink }}
        />
      </Col>
      <Col md={8} xs={24}>
        <SearchField
          GET_QUERY={SEARCH_REFERRAL_SUB_SOURCES}
          label={t('app.referralSubtype')}
          fieldName="referralSubSource"
          dataName="searchReferralSubSources"
          rules={[{ required: !!referralSourceData?.forceSubtype, message: t('app.fieldRequired') }]}
          disabled={!referralSource}
          allowClear
          passToQuery={{ referralSource: referralSource?.key, leadLink }}
          setFieldsValue={setFieldsValue}
        />
      </Col>
      {user && referralSourceData?.forUsers && !hideUser && (
        <Col md={8} xs={24}>
          <SearchField
            GET_QUERY={SEARCH_USERS_QUERY}
            label={t('logs.selectUser')}
            fieldName="referralUser"
            dataName="searchUsers"
            formName="patientForm"
          />
        </Col>
      )}
      {user && referralSourceData?.forPatients && !hidePatient && (
        <Col md={8} xs={24}>
          <SearchField
            GET_QUERY={SEARCH_PATIENT_QUERY}
            label={t('common.selectPatient')}
            fieldName="referralPatient"
            dataName="searchPatients"
            formName="patientForm"
          />
        </Col>
      )}
      {referralDetailsVisible ? (
        <Col md={8} xs={24}>
          <NameField
            required={!!referralDetailsRequired}
            fieldName="referralDetails"
            label={t('patients.referralDetails')}
            autoComplete="off"
            min={0}
            max={200}
          />
        </Col>
      ) : null}
    </Row>
  )
}

ReferralSourceField.propTypes = {
  form: PropTypes.object.isRequired,
  required: PropTypes.bool,
  visible: PropTypes.bool,
  companyId: PropTypes.string,
  leadLink: PropTypes.string
}

export default ReferralSourceField
